// Client-side helpers for detecting when the athlete's saved trainingWeek is
// out of sync with the current TrainingPeaks calendar, and for rebuilding it
// from events (mirrors the logic in Step4Training so the Tracker can offer a
// single-tap re-import).
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseLocal = (iso) => { const [y, m, dd] = iso.split("-").map(Number); return new Date(y, m - 1, dd); };
const addDaysISO = (iso, n) => { const d = parseLocal(iso); d.setDate(d.getDate() + n); return toISO(d); };
const dayDiff = (a, b) => Math.round((parseLocal(a) - parseLocal(b)) / 86400000);

const MAX_SESSIONS = 4;

const emptySessionFromEvent = (e, paceBySport) => {
  let duration = e.durationMin || 0;
  let estimated = false;
  if (!duration && e.distanceMi && paceBySport?.[e.sport]) {
    duration = Math.round(e.distanceMi * paceBySport[e.sport]);
    estimated = true;
  } else if (!duration) {
    estimated = true;
  }
  return {
    sport: e.sport || "other",
    duration: Math.max(0, Math.min(1440, duration)),
    intensity: e.intensity || "endurance",
    timing: "AM", terrain: "flat", heat: false, key: false, mealStyle: "3-meals",
    tpEstimated: estimated,
    tpDistanceMi: e.distanceMi || null,
    tpTitle: e.title || null,
  };
};

// TrainingPeaks "Day Off" / "Rest Day" / recovery-only entries should not
// count as trainable sessions — they'd otherwise inflate demand, hit the
// "TRAINING TODAY" strap on the Tracker, and misapply carb-fuelling logic
// (beta feedback Feb 27 2026: "rest day says training today"). We treat
// them as rest markers only.
const REST_MARKER_RE = /\b(day\s*off|rest\s*day|extra\s*rest|no\s*training|recovery\s*day|off\s*day)\b/i;
export function isRestMarkerEvent(e) {
  if (!e) return false;
  const title = (e.title || "").toString();
  const wtype = (e.wtype || e.workoutType || "").toString();
  if (REST_MARKER_RE.test(title)) return true;
  if (REST_MARKER_RE.test(wtype)) return true;
  // Zero-duration + zero-distance events are calendar markers, not sessions.
  const dur = parseInt(e.durationMin, 10) || 0;
  const dist = parseFloat(e.distanceMi) || 0;
  if (dur === 0 && dist === 0) return true;
  return false;
}

export function buildDaysFromTpEvents(events, weekStart, paceBySport) {
  const newDays = Array.from({ length: 7 }, () => ({ sessions: [] }));
  (events || []).forEach((e) => {
    if (isRestMarkerEvent(e)) return;
    const di = dayDiff(e.date, weekStart);
    if (di < 0 || di > 6 || newDays[di].sessions.length >= MAX_SESSIONS) return;
    newDays[di].sessions.push(emptySessionFromEvent(e, paceBySport));
  });
  return newDays;
}

// Compare the plan's saved trainingWeek days vs the live TP-derived days.
// Returns { changed: boolean, changedDays: [{ index, savedMins, liveMins }] }.
// A day is considered changed if its session count or total minutes differ.
export function diffTrainingWeek(savedDays, liveDays) {
  const changedDays = [];
  for (let i = 0; i < 7; i++) {
    const s = (savedDays?.[i]?.sessions || []).filter((x) => (parseInt(x.duration, 10) || 0) > 0);
    const l = (liveDays?.[i]?.sessions || []).filter((x) => (parseInt(x.duration, 10) || 0) > 0);
    const sMins = s.reduce((a, x) => a + (parseInt(x.duration, 10) || 0), 0);
    const lMins = l.reduce((a, x) => a + (parseInt(x.duration, 10) || 0), 0);
    if (s.length !== l.length || sMins !== lMins) {
      changedDays.push({ index: i, savedMins: sMins, liveMins: lMins, savedCount: s.length, liveCount: l.length });
    }
  }
  return { changed: changedDays.length > 0, changedDays };
}

export { addDaysISO };
